import axios from 'axios';
import { getTenantBaseUrl } from '@/utils/tenant';

/**
 * Recipient-facing share API (/api/shared/*).
 *
 * Its own axios instance, not the shared apiService. That one treats a 401 as an
 * expired staff session — it clears the token, redirects to the firm's login
 * page, and swallows the rejection so the caller never hears back. Here a 401
 * just means "PIN again". Errors reach the caller untouched.
 */
const shareClient = axios.create({
  baseURL: getTenantBaseUrl(),
});

const SESSION_KEY = 'share_session';

export const getShareSession = (token) => {
  try {
    const raw = sessionStorage.getItem(`${SESSION_KEY}:${token}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed.expires_at && new Date(parsed.expires_at) < new Date()) {
      sessionStorage.removeItem(`${SESSION_KEY}:${token}`);
      return null;
    }
    return parsed.session_token;
  } catch {
    return null;
  }
};

export const setShareSession = (token, session) => {
  sessionStorage.setItem(`${SESSION_KEY}:${token}`, JSON.stringify(session));
};

export const clearShareSession = (token) => {
  sessionStorage.removeItem(`${SESSION_KEY}:${token}`);
};

const withSession = (token) => ({
  headers: { 'X-Share-Session': getShareSession(token) ?? '' },
});

/** Bytes that come through our API, which needs the session header. */
const getBlob = async (token, path) => {
  try {
    const response = await shareClient.get(path, { ...withSession(token), responseType: 'blob' });
    return response.data;
  } catch (error) {
    throw await withReadableBlobError(error);
  }
};

const publicShareService = {
  /** Space name and expiry for the PIN screen — no session needed. */
  async getSummary(token) {
    const response = await shareClient.get(`/shared/${token}`);
    return response.data.data;
  },

  async unlock(token, pin) {
    const response = await shareClient.post(`/shared/${token}/unlock`, { pin });
    const session = response.data.data;
    setShareSession(token, session);
    return session;
  },

  async getTree(token) {
    const response = await shareClient.get(`/shared/${token}/tree`, withSession(token));
    return response.data.data;
  },

  async getPreview(token, fileId) {
    const response = await shareClient.get(
      `/shared/${token}/files/${fileId}/preview`,
      withSession(token),
    );
    return response.data.data;
  },

  /**
   * A PDF's bytes for the View Only viewer (OL-166). They come through our API
   * because B2 sends no CORS headers, so the viewer can't read a presigned link.
   */
  getPdf(token, fileId) {
    return getBlob(token, `/shared/${token}/files/${fileId}/pdf`);
  },

  async getDownloadUrl(token, fileId) {
    const response = await shareClient.get(
      `/shared/${token}/files/${fileId}/download`,
      withSession(token),
    );
    return response.data.data.url;
  },

  /**
   * Folder (or whole-space, folderId omitted) zip. The bytes come from our API
   * and need the session header, so this is a blob fetch — a presigned URL in
   * a new tab would not carry X-Share-Session.
   */
  downloadFolderZip(token, folderId = null) {
    const path = folderId
      ? `/shared/${token}/folders/${folderId}/download`
      : `/shared/${token}/download`;

    return getBlob(token, path);
  },
};

/**
 * Under responseType blob, an error body arrives as a Blob, which hides
 * response.data.message from every caller. Re-inflate it to JSON.
 */
const withReadableBlobError = async (error) => {
  const body = error?.response?.data;
  if (body instanceof Blob) {
    try {
      error.response.data = JSON.parse(await body.text());
    } catch {
      /* not JSON — leave the blob in place */
    }
  }
  return error;
};

export default publicShareService;
